import type { Carer } from '@lmc/ts-common'
import { Button } from '@lmc/ui-kit'
import { Input, Modal } from 'antd'
import { showToast } from 'common/utils'
import useUser from 'hooks/useUser/useUser'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { reportsConstants } from 'locale/team'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

export type DeleteAccountModalProps = {
  open: boolean
  carer: Carer | null
  onClose: () => void
  // Called after a successful delete so the consumer can refresh the team list
  // (the list's data is cached under a separate query key and isn't invalidated
  // by the delete mutation).
  onDeleted?: () => void
}

const TEST_ID = 'delete-account-modal'

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Title = styled.div`
  font-weight: 500;
  font-size: 24px;
  line-height: 32px;
  color: ${({ theme }) => theme.color.neutral.color2};
`

const BulletList = styled.ul`
  margin: 0;
  padding-left: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FieldLabel = styled.div`
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const FooterRow = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

// All stateful behaviour lives in the inner component so it can be keyed by
// carer.id and re-mounted fresh per member — the typed-name input resets and
// the delete mutation always targets the carer just opened.
const DeleteAccountContent = ({
  carer,
  onClose,
  onDeleted
}: {
  carer: Carer
  onClose: () => void
  onDeleted?: () => void
}) => {
  const { t } = useTranslation(NAMESPACE.TEAM)
  const { t: tGeneral } = useTranslation(NAMESPACE.GENERAL)

  const fullName = `${carer.name?.first ?? ''} ${carer.name?.last ?? ''}`.trim()

  const [typed, setTyped] = useState('')
  const matches = typed.trim() === fullName

  const { deleteUser, isLoadingDelete } = useUser({
    id: carer.id,
    // The modal already has the carer from the list and only needs the delete
    // mutation, so skip useUser's carer fetch.
    options: { enabled: false },
    onDeleteSuccess: () => {
      showToast({
        title: t(reportsConstants.DELETE_ACCOUNT_DELETED_TOAST),
        type: 'success'
      })
      onDeleted?.()
      onClose()
    }
  })

  const handleDelete = () => {
    if (!matches) {
      return
    }
    deleteUser()
  }

  return (
    <Body data-testid={TEST_ID}>
      <Title>{t(reportsConstants.DELETE_ACCOUNT_TITLE)}</Title>
      <div>
        <Trans
          ns={NAMESPACE.TEAM}
          i18nKey={reportsConstants.DELETE_ACCOUNT_BODY}
          values={{ name: fullName }}
          components={{ strong: <strong /> }}
        />
      </div>
      <BulletList>
        <li>{t(reportsConstants.DELETE_ACCOUNT_BULLET_1)}</li>
        <li>{t(reportsConstants.DELETE_ACCOUNT_BULLET_2)}</li>
        <li>{t(reportsConstants.DELETE_ACCOUNT_BULLET_3)}</li>
      </BulletList>
      <Field>
        <FieldLabel>
          {t(reportsConstants.DELETE_ACCOUNT_CONFIRM_LABEL)}
        </FieldLabel>
        <Input
          value={typed}
          placeholder={fullName}
          onChange={(e) => setTyped(e.target.value)}
          aria-label={t(reportsConstants.DELETE_ACCOUNT_CONFIRM_LABEL)}
          data-testid={`${TEST_ID}-input`}
        />
      </Field>
      <FooterRow>
        <Button onClick={onClose} data-testid={`${TEST_ID}-cancel`}>
          {tGeneral(generalConstants.CANCEL)}
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={!matches || isLoadingDelete}
          isLoading={isLoadingDelete}
          data-testid={`${TEST_ID}-confirm`}
        >
          {t(reportsConstants.DELETE_ACCOUNT_CTA)}
        </Button>
      </FooterRow>
    </Body>
  )
}

const DeleteAccountModal = ({
  open,
  carer,
  onClose,
  onDeleted
}: DeleteAccountModalProps) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width={580}
    destroyOnClose
    data-testid={`${TEST_ID}-dialog`}
  >
    {carer && (
      <DeleteAccountContent
        key={carer.id}
        carer={carer}
        onClose={onClose}
        onDeleted={onDeleted}
      />
    )}
  </Modal>
)

export default DeleteAccountModal
